import type { RequiredGeneric } from '@pba/react-utils';
import type { LabelInValue, NodeOrganization } from 'models';
import type { ReactNode } from 'react';
import { Checkbox, Col, Form, FormSection, Icon, Row, TagsInput, Text, Tooltip } from 'ui';
// eslint-disable-next-line @typescript-eslint/no-restricted-imports -- TODO: Break circular dependency
import { NodeOrganizationPicker } from 'features/organizations/node-organization-picker';
import { useCreateOrganizationOption, useTagsRules } from './hooks';

export type ExtendedChannelFormContentValues = {
  featuredInChannels?: boolean;
  organization?: OrganizationOption;
  showInChannels: boolean;
  tags?: string[];
};

export type OrganizationOption = { optionLabel: ReactNode; originalOrganization: NodeOrganization } & LabelInValue;

const ChannelFormContent = <T extends ExtendedChannelFormContentValues = never>(_: RequiredGeneric<T>) => {
  const shouldShowInChannels = Form.useWatch<ExtendedChannelFormContentValues['showInChannels']>('showInChannels');
  const organization = Form.useWatch<ExtendedChannelFormContentValues['organization']>('organization');
  const createOrganizationOption = useCreateOrganizationOption();
  const tagsRules = useTagsRules();

  return (
    <FormSection title={{ children: 'Channels', icon: <Icon icon="compass-line" /> }}>
      <Form.Item<ExtendedChannelFormContentValues> name="showInChannels" valuePropName="checked">
        <Checkbox>
          <Row gutter="xs">
            <Col>
              <Text size="small">Show event in Channels (Optional)</Text>
            </Col>
            <Col>
              <Tooltip title="Event will appear in Channels and become public. Anyone will be able to join.">
                <Icon icon="information-line" />
              </Tooltip>
            </Col>
          </Row>
        </Checkbox>
      </Form.Item>
      {shouldShowInChannels ? (
        <>
          <Form.Item<ExtendedChannelFormContentValues>
            label="Organization"
            name="organization"
            rules={[{ required: true }]}
          >
            <NodeOrganizationPicker<OrganizationOption>
              createOption={createOrganizationOption}
              fieldNames={{ label: 'optionLabel' }}
            />
          </Form.Item>
          {organization?.originalOrganization.settings?.allowCustomChannels ? (
            <TagsInput
              input={{
                options: organization.originalOrganization.channelTags?.map((tag) => ({
                  label: tag.name,
                  value: tag.name,
                })),
              }}
              item={{
                extra: 'Tags appear for organizations that are using custom channels',
                label: 'Tags',
                name: 'tags',
                rules: tagsRules,
              }}
            />
          ) : null}
          <Form.Item<ExtendedChannelFormContentValues> name="featuredInChannels" valuePropName="checked">
            <Checkbox>
              <Row gutter="xs">
                <Col>
                  <Text size="small">Pin event as featured in Channels</Text>
                </Col>
                <Col>
                  <Tooltip title="Event will appear in main Channels page">
                    <Icon icon="information-line" />
                  </Tooltip>
                </Col>
              </Row>
            </Checkbox>
          </Form.Item>
        </>
      ) : null}
    </FormSection>
  );
};

export default ChannelFormContent;
